import {
  Alert,
  Autocomplete,
  Button,
  Container,
  Group,
  Loader,
  Modal,
  Select,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { useAssignUserToGatewayGroupMutation } from '../../api/assignments';
import { IGatewayGroup } from '../../api/types';
import { useGetUsersQuery } from '../../api/users';
import { rolesData } from '../../Permissions/GatewayRoles';

type Props = {
  gatewayGroup: IGatewayGroup;
  onClose: () => void;
};

interface DisplayUser {
  label: string;
  value: string;
  userid: string;
}

function AssignUserModal({ onClose, gatewayGroup }: Props) {
  const [isAdding, setIsAdding] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const allUsers = useGetUsersQuery();
  const [addUser] = useAssignUserToGatewayGroupMutation();
  const [users, setUsers] = useState<DisplayUser[]>([]);

  useEffect(() => {
    setUsers(
      (allUsers?.data?.Users ?? [])
        .map((e) => {
          return {
            userid: e.id.toString(),
            label: e.name,
            value: e.name,
          } as DisplayUser;
        })
        .sort((a, b) => a.label?.localeCompare(b.label ?? '') ?? 0)
    );
  }, [allUsers.data]);

  return (
    <>
      <Modal
        opened={true}
        onClose={() => onClose()}
        title={`Assign User to ${gatewayGroup.name}`}
      >
        {allUsers.isFetching ? (
          <Loader></Loader>
        ) : (
          <>
            <Autocomplete
              data-testid="new-user"
              label="User"
              placeholder="Select a person to add"
              filter={(value, item) =>
                item.label.toLowerCase().includes(value.toLowerCase())
              }
              onItemSubmit={(e) => {
                setSelectedUserId(e.userid);
              }}
              nothingFound="No matching people"
              data={users}
            />
            <Select
              label="Role"
              data-testid="new-role"
              data={rolesData}
              defaultValue={'Viewer'}
              onChange={(e) => setSelectedRoleId(e ?? '')}
            />
            {errorMessage && (
              <Container p="md">
                <Alert color={'red'}>{errorMessage}</Alert>
              </Container>
            )}
            <Group pt="md" position="right">
              <Button variant="outline" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                data-testid="add-user"
                disabled={isAdding}
                color="dark"
                onClick={() => {
                  setIsAdding(true);
                  setErrorMessage('');
                  addUser({
                    userId: selectedUserId,
                    roleId: parseInt(selectedRoleId),
                    gatewayGroupid: gatewayGroup.id,
                  })
                    .unwrap()
                    .then(
                      () => {
                        onClose();
                      },
                      (e) => setErrorMessage(e.data?.message)
                    )
                    .finally(() => setIsAdding(false));
                }}
              >
                Add {isAdding && <Loader size={'sm'}></Loader>}
              </Button>
            </Group>
          </>
        )}
      </Modal>
    </>
  );
}

export default AssignUserModal;
